/**
 * Fetches embedding results from a Sanity API endpoint based on a query.
 * This function queries the embeddings API and returns the relevant data.
 *
 * @param query - The query string to search for in the embeddings index.
 * @returns An array of query results or an empty array if no results are found.
 */
export const getEmbeddingResult = async (query: string) => {
  const { NUXT_PROJECT_ID, NUXT_DATASET, NUXT_EMBEDDINGS_INDEX_NAME, NUXT_EMBEDDINGS_API_TOKEN } =
    useRuntimeConfig().public

  const endpoint = `https://${NUXT_PROJECT_ID}.apicdn.sanity.io/vX/embeddings-index/query/${NUXT_DATASET}/${NUXT_EMBEDDINGS_INDEX_NAME}`
  const response = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${NUXT_EMBEDDINGS_API_TOKEN}`, // Include the API Read token for authorization.
    },
    body: JSON.stringify({
      query, // The query string to search for in the embeddings.
      maxResults: 10, // Limit the number of results.
      filter: {}, // Any additional filters.
    }),
  })

  if (!response.ok) {
    const errorDetails = await response.text()
    throw new Error(`API Error: ${response.status} - ${errorDetails}`)
  }

  const data = await response.json()

  if (!Array.isArray(data)) return []

  // Extract document IDs from the response data and filter out invalid entries.
  const documentIds = data
    .map((item) => item?.value?.documentId) // Extract documentId from each result.
    .filter((id): id is string => Boolean(id)) // Ensure only valid IDs are included.

  // Return the query data for the relevant pages.
  return documentIds
}
